import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks, faRefresh, faBell } from '@fortawesome/free-solid-svg-icons';
import { Client, QBOCompanyData } from '@types';
import { QBOCategory, QBODetailedTransaction } from '@shared/types/qbo_data';
import { Button } from 'react-daisyui';
import { useMemo, useState } from 'react';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import PageContainer from '@/components/PageContainer';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { get, post, put } from '@/utils/queries';
import { alertErrorMessage, alertMessageSuccess } from '@/utils/alerts';
import { LoadingPage } from '@/pages/LoadingPage';
import { EmptyState } from '@/components/EmptyState';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';
import { Spinner } from '@/components/Spinner';
import { get as getApi } from '@/utils/api';
import { TableTransactionsClient } from './TableTransactionsClient';
import { TableTransactionsForControllers } from './TableTransactionsForControllers';
import { CompanySelectorDropdown } from './CompanySelector';
import { TransactionAutoComplete } from './TransactionAutoComplete';
import { MemoActionSelect } from './transactionsComponents';
import ModalReminder from './ModalReminder';

export default function Transactions() {
  const [refreshInProgress, setRefreshInProgress] = useState<boolean>(false);
  const [selectedQBOCompanyId, setSelectedQBOCompanyId] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkEditMode, setBulkEditMode] = useState<boolean>(false);
  const [batchKeepOriginal, setBatchKeepOriginal] = useState<boolean>(false);
  const [batchCategory, setBatchCategory] = useState<QBOCategory | undefined>();
  const { companyName, impersonatingAccountId, auth }: IUserContext =
    useUserContext();

  const transactionsListAPIUrl = '/transactions/fulldata';
  const {
    data: qboData,
    isValidating: isLoading,
    error,
    mutate: mutateQboData,
  } = impersonatingAccountId
    ? get<QBOCompanyData[]>(
        `${transactionsListAPIUrl}/${impersonatingAccountId}`
      )
    : get<QBOCompanyData[]>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${transactionsListAPIUrl}/${auth?.clients?.[0]?.id}`
      );

  const [isSavingReview, setIsSavingReview] = useState<boolean>(false);
  const [isPushing, setIsPushing] = useState<boolean>(false);

  // Persist a reviewer's decision. Mirrors deleteQBOCompany: build the expected
  // data first, call the API, and only patch the cache (no refetch) on success.
  const saveReview = async (reviewed: QBODetailedTransaction) => {
    const qboDataAfterReview = (qboData ?? []).map((company) => ({
      ...company,
      qbo_transactions: company.qbo_transactions.map((t) =>
        t.id === reviewed.id ? reviewed : t
      ),
    }));

    setIsSavingReview(true);
    try {
      await put(`transactions/${reviewed.id as number}/review`, {
        client_id: reviewed.client_id,
        keep_original_memo: !!reviewed.keep_original_memo,
        reviewed_qbo_categories_id: reviewed.reviewed_qbo_categories_id ?? null,
      });
      await mutateQboData(qboDataAfterReview, {
        revalidate: false,
        optimisticData: qboDataAfterReview,
      });
      alertMessageSuccess('Review saved.');
    } catch (err) {
      alertErrorMessage('Error while saving the review, try again later.');
    } finally {
      setIsSavingReview(false);
    }
  };

  const { data: clients } = get<Client[]>('account/list');

  const sortedCompanies = useMemo(
    () => {
      if (qboData && !isLoading) {
        return [...qboData].sort(
          (a, b) => b.qbo_transactions.length - a.qbo_transactions.length
        );
      }
      return [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qboData]
  );

  const selectedCompany = useMemo(
    () =>
      sortedCompanies.find((c) => c.id === selectedQBOCompanyId) ??
      sortedCompanies[0],
    [sortedCompanies, selectedQBOCompanyId]
  );

  const refreshQBOData = async () => {
    setRefreshInProgress(true);

    const clientId = impersonatingAccountId || auth?.clients?.[0]?.id;
    try {
      await getApi(`transactions/syncQBO/${clientId as number}`);
    } finally {
      setTimeout(() => setRefreshInProgress(false), 300);
    }
  };

  const handleCompanySelect = (companyId: number) => {
    setSelectedQBOCompanyId(companyId);
    // Selection and its batch controls are per company, so reset them when
    // switching companies (categories in particular are company-scoped).
    setSelectedIds(new Set());
    setBulkEditMode(false);
    setBatchCategory(undefined);
    setBatchKeepOriginal(false);
  };

  if (!qboData && isLoading) {
    return <LoadingPage hasNav />;
  }

  if (error) {
    return (
      <PageContainer name='client-list'>
        <DashboardWrapper
          companyName={companyName as string}
          companyData={clients}
        >
          <ErrorAlert
            isSuperAdmin={auth?.isSuperAdmin}
            error={error as unknown}
            context='Transactions'
          />
        </DashboardWrapper>
      </PageContainer>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (!sortedCompanies.length) {
    return (
      <PageContainer name='client-list'>
        <DashboardWrapper
          companyName={companyName as string}
          companyData={clients}
        >
          <EmptyState name='transactions' />
        </DashboardWrapper>
      </PageContainer>
    );
  }

  const adminTransactions = selectedCompany?.qbo_transactions ?? [];
  const respondedTransactions = adminTransactions.filter(
    (t) => !!t.modified_memo || !!t.modified_qbo_categories_id
  );
  const pendingTransactions = adminTransactions.filter(
    (t) => !t.modified_memo && !t.modified_qbo_categories_id
  );

  // Pushing needs a reviewed category, so the push action is only enabled once
  // every selected transaction has one (bulk edit has no such requirement).
  const selectedTransactions = adminTransactions.filter((t) =>
    selectedIds.has(t.id as number)
  );
  const allSelectedReviewed =
    selectedTransactions.length > 0 &&
    selectedTransactions.every((t) => !!t.reviewed_qbo_categories_id);

  const onToggleSelect = (id: number, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const onSelectMany = (ids: number[], checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });

  // Push the reviewed transactions to QBO. The backend removes the ones that
  // went through, so we drop those from the cache and the selection.
  const pushToQBO = async (ids: number[]) => {
    if (ids.length === 0 || !selectedCompany) return;
    const clientId = selectedCompany.client_id;

    setIsPushing(true);
    try {
      const res = await post<{
        message: string;
        results: { transactionId: number; updated: boolean }[];
      }>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `transactions/pushTransactions/${clientId as number}/${
          selectedCompany.id as number
        }`,
        { transactionIds: ids }
      );

      const pushedIds = (res.results ?? [])
        .filter((r) => r.updated)
        .map((r) => r.transactionId);

      // Optimistic version (kept for later): drops the pushed rows from the
      // cache without a refetch.
      // const qboDataAfterPush = (qboData ?? []).map((company) => ({
      //   ...company,
      //   qbo_transactions: company.qbo_transactions.filter(
      //     (t) => !pushedIds.includes(t.id as number)
      //   ),
      // }));
      // await mutateQboData(qboDataAfterPush, {
      //   revalidate: false,
      //   optimisticData: qboDataAfterPush,
      // });

      setSelectedIds((prev) => {
        const next = new Set(prev);
        pushedIds.forEach((id) => next.delete(id));
        return next;
      });
      alertMessageSuccess(res.message || 'Transactions pushed to QBO.');
    } catch (err) {
      alertErrorMessage(
        'Error while pushing transactions to QBO, try again later.'
      );
    } finally {
      // Refetch either way: the ones that went through leave the list, and the
      // ones that failed come back with an updated `tries` (error indicator).
      // Runs even when every push failed (the request rejects with a 502).
      try {
        await mutateQboData();
      } catch {
        alertErrorMessage(
          'Could not refresh the transactions list. Please reload the page.'
        );
      }
      setIsPushing(false);
    }
  };

  // Bulk-apply the review (one memo decision + one category) to every selected
  // transaction and push them to QBO in a single request. The backend resolves
  // the per-row memo fallback from `modified_memo`, so we only send the intent.
  // Mirrors pushToQBO: refetch in `finally` so the error indicators stay fresh.
  const applyBatchReview = async () => {
    if (!selectedCompany || !batchCategory?.id || selectedIds.size === 0)
      return;
    const clientId = selectedCompany.client_id;
    const ids = [...selectedIds];

    setIsPushing(true);
    try {
      const res = await post<{
        message: string;
        results: { transactionId: number; updated: boolean }[];
      }>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `transactions/reviewAndPush/${clientId as number}/${
          selectedCompany.id as number
        }`,
        {
          transactionIds: ids,
          keep_original_memo: batchKeepOriginal,
          reviewed_qbo_categories_id: batchCategory.id,
        }
      );

      const pushedIds = (res.results ?? [])
        .filter((r) => r.updated)
        .map((r) => r.transactionId);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pushedIds.forEach((id) => next.delete(id));
        return next;
      });
      alertMessageSuccess(res.message || 'Changes applied and pushed to QBO.');
    } catch (err) {
      alertErrorMessage('Error while applying the changes, try again later.');
    } finally {
      // The refetch runs regardless of the POST outcome. If it fails the list
      // on screen may be stale, so surface a reload hint instead of silently
      // swallowing it — but always reset below so the spinner never stays stuck.
      try {
        await mutateQboData();
      } catch {
        alertErrorMessage(
          'Could not refresh the transactions list. Please reload the page.'
        );
      }
      setBulkEditMode(false);
      setBatchCategory(undefined);
      setBatchKeepOriginal(false);
      setIsPushing(false);
    }
  };

  const selectionBarClass =
    'sticky top-2 z-40 mb-3 flex items-center justify-between gap-4 ' +
    'rounded-lg border border-accent/40 bg-base-100 px-4 py-2 shadow-md';

  return (
    <PageContainer name='transactions'>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
      <DashboardWrapper
        companyName={companyName as string}
        companyData={clients}
      >
        {auth?.isAdmin ? (
          <ModalReminder
            clientId={impersonatingAccountId || auth?.clients?.[0]?.id}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
          />
        ) : null}
        <div className='p-4'>
          <div className='flex w-full justify-between items-center mb-2.5'>
            <h1 className='text-lg font-bold uppercase'>
              <FontAwesomeIcon
                icon={faTasks}
                size='lg'
                className='mr-4 text-white'
              />
              Help us Categorize these transactions
            </h1>
            {auth?.isAdmin ? (
              <div>
                <Button
                  color='accent'
                  id='reminder'
                  title='reminder'
                  size='sm'
                  className='btn btn-accent mr-2'
                  data-testid='reminder-button'
                  onClick={() => {
                    setIsOpen(true);
                  }}
                >
                  <FontAwesomeIcon icon={faBell} size='lg' className='mr-2' />
                  Send task reminder
                </Button>
                <Button
                  color='accent'
                  id='refresh'
                  size='sm'
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={() => refreshQBOData()}
                  data-testid='refresh-button'
                >
                  <FontAwesomeIcon
                    icon={faRefresh}
                    size='lg'
                    className={`mr-2 text-white ${
                      refreshInProgress ? 'spinning' : ''
                    }`}
                  />
                  Refresh QBO Data
                </Button>
              </div>
            ) : null}
          </div>
          <CompanySelectorDropdown
            companies={sortedCompanies}
            selectedId={selectedCompany?.id}
            onSelect={handleCompanySelect}
          />
          <div className='text-sm my-2 capitalize font-bold'>
            Transctions for: {selectedCompany?.name} - Pending Transactions:{' '}
            {selectedCompany?.pending_transactions}
          </div>
          {auth?.isAdmin ? (
            <>
              {isSavingReview || isPushing ? (
                <div
                  className='fixed inset-0 z-[999] flex items-center justify-center bg-black/50'
                  data-testid='spinner-transaction'
                >
                  <Spinner className='h-1/4' />
                </div>
              ) : null}
              <hr />
              <br />
              {selectedIds.size > 0 ? (
                <div className={selectionBarClass}>
                  <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
                    <span className='text-sm font-semibold whitespace-nowrap'>
                      {selectedIds.size} selected
                    </span>
                    {bulkEditMode ? (
                      <>
                        <div className='flex items-center gap-2'>
                          <span className='text-xs font-semibold uppercase text-base-content/60'>
                            Approve Memo
                          </span>
                          <MemoActionSelect
                            testId='batch-memo-select'
                            keepOriginal={batchKeepOriginal}
                            onChange={setBatchKeepOriginal}
                          />
                        </div>
                        <div className='flex items-center gap-2'>
                          <span className='text-xs font-semibold uppercase text-base-content/60'>
                            Set Category
                          </span>
                          <div className='w-56'>
                            <TransactionAutoComplete
                              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                              key={`batch-cat-${selectedCompany?.id}`}
                              value={batchCategory?.name}
                              onOptionSelected={(c) => setBatchCategory(c)}
                              clientId={selectedCompany?.client_id as number}
                              qboCompanyId={selectedCompany?.id as number}
                            />
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>

                  <div className='flex shrink-0 items-center gap-2'>
                    {bulkEditMode ? (
                      <>
                        <Button
                          size='sm'
                          className='btn-secondary'
                          data-testid='batch-apply'
                          disabled={!batchCategory?.id}
                          // eslint-disable-next-line @typescript-eslint/no-misused-promises
                          onClick={applyBatchReview}
                        >
                          Apply to {selectedIds.size}
                        </Button>
                        <Button
                          size='sm'
                          className='btn-ghost'
                          data-testid='bulk-edit-cancel'
                          onClick={() => setBulkEditMode(false)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size='sm'
                          className='btn-outline'
                          data-testid='bulk-edit'
                          onClick={() => setBulkEditMode(true)}
                        >
                          Bulk edit and push
                        </Button>
                        <span
                          className={
                            allSelectedReviewed ? '' : 'tooltip tooltip-top'
                          }
                          data-tip='Every selected transaction needs a reviewed category to push.'
                        >
                          <Button
                            color='accent'
                            size='sm'
                            data-testid='push-to-qbo'
                            disabled={!allSelectedReviewed}
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onClick={async () => {
                              await pushToQBO([...selectedIds]);
                            }}
                          >
                            Push {selectedIds.size} to QBO
                          </Button>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ) : null}
              <div className='text-lg my-2 capitalize font-bold'>
                Client responded ({respondedTransactions.length})
              </div>
              <TableTransactionsForControllers
                transactions={respondedTransactions}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                onSelectMany={onSelectMany}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSaveReview={async (transaction) => {
                  await saveReview(transaction);
                }}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onPushToQBO={async (ids) => {
                  await pushToQBO(ids);
                }}
              />
              <div className='text-lg mt-6 mb-2 capitalize font-bold'>
                Pending response ({pendingTransactions.length})
              </div>
              <TableTransactionsForControllers
                transactions={pendingTransactions}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                onSelectMany={onSelectMany}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSaveReview={async (transaction) => {
                  await saveReview(transaction);
                }}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onPushToQBO={async (ids) => {
                  await pushToQBO(ids);
                }}
              />
            </>
          ) : (
            <TableTransactionsClient
              transactions={selectedCompany?.qbo_transactions ?? []}
            />
          )}
        </div>
      </DashboardWrapper>
    </PageContainer>
  );
}
